"use client"

import {
  difficultyLevels,
  durationOptions,
  priceRanges,
  priceRangesVI,
  tripTypes,
} from "@/(admin)/modules/ui-builder/utils/defaultFilter"
import TourCard from "@/components/tours/tour-card"
import { t } from "@/languages/i18n"
import { TourDetail } from "@/types/tour"
import { DestinationItem, TourItem } from "@/types/types"
import { LoadingOutlined } from "@ant-design/icons"
import { ComponentConfig } from "@measured/puck"
import { Pagination, Spin } from "antd"
import { ChevronDown } from "lucide-react"
import { usePathname, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import { fetchDestinations } from "../../../../../destination/services/destination"
import { fetchToursV2 } from "../../../../../tour/services/tours"
import "./customSwiper.css"

export type PostGridByCategoryProps = {
  categoryId?: number
  tag?: string
  isAutoPlay?: boolean
  isLoopImage?: boolean
  colorPrimaryTag?: string
  itemsToRender?: number | "All"
  showSearch?: boolean
  showTitle?: boolean
}

export const RenderGridTours: ComponentConfig<PostGridByCategoryProps> = {
  render: ({ categoryId, tag, isAutoPlay, isLoopImage, colorPrimaryTag, itemsToRender, showSearch, showTitle }) => {
    const [toursData, setToursData] = useState<TourDetail[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedDestination, setSelectedDestination] = useState<string[]>([])
    const [selectedTripType, setSelectedTripType] = useState<string[]>([])
    const [selectedPrice, setSelectedPrice] = useState<string[]>([])
    const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([])
    const [selectedDuration, setSelectedDuration] = useState<string[]>([])
    const [destinations, setDestinations] = useState<DestinationItem[]>([])
    const [lang, setLang] = useState<string>()
    const [isScrolledToFilter, setIsScrolledToFilter] = useState(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalTours, setTotalTours] = useState<number>(0)
    const filterRef = useRef<HTMLDivElement>(null)
    const filterInitialTopRef = useRef<number | null>(null)
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const searchValue = searchParams.get("search") ?? ""
    const currentLocale = typeof document !== "undefined" ? document.documentElement.lang || "en" : "en"
    const isVI = currentLocale?.toLowerCase().startsWith("vi")
    const priceRangesData = isVI ? priceRangesVI : priceRanges
    const showDurationFilter = pathname?.includes("all-tours")
    const pageSize = 9

    // Tạo duration options bằng hàm map

    const transformApiDataTours = (data: TourItem[]) => {
      const transformData: TourDetail[] =
        data &&
        data.map(item => {
          const { attributes } = item
          // Parse price_overview nếu là string, giữ nguyên nếu là number
          let priceOverview: number | undefined
          if (attributes?.price_overview !== undefined && attributes?.price_overview !== null) {
            if (typeof attributes.price_overview === "string") {
              // Extract số từ string (ví dụ: "1000 / person" -> 1000)
              const priceMatch = attributes.price_overview.match(/[\d,]+/)
              priceOverview = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, "")) : undefined
            } else if (typeof attributes.price_overview === "number") {
              priceOverview = attributes.price_overview
            }
          }

          return {
            feature_img: (() => {
              const img = attributes?.feature_img
              if (!img) return []
              // Nếu là string, convert thành array
              if (typeof img === "string") return [img]
              // Nếu là array, map và xử lý từng phần tử
              if (Array.isArray(img)) {
                return img.map(item => (typeof item === "string" ? item : item?.url || item))
              }
              return []
            })(),
            is_new: attributes?.is_new ?? false,
            is_most_popular: attributes?.is_most_popular ?? false,
            name: attributes?.name ?? attributes?.title ?? "Tours",
            description: attributes?.description ?? attributes?.short_description ?? "",
            price_overview: priceOverview,
            distance: attributes?.distance ?? "",
            duration: attributes?.duration ?? "",
            slug: attributes?.slug ?? "",
          }
        })

      setToursData(transformData)
    }

    const fetchToursList = async (
      searchValue?: string,
      customFilters?: {
        destination?: string[]
        tripType?: string[]
        price?: string[]
        difficulty?: string[]
        duration?: string[]
      },
      page: number = 1,
    ) => {
      setLoading(true)

      const filter = {
        searchValue: searchValue,
        category: categoryId,
        tag: tag,
        destination: customFilters?.destination ?? selectedDestination,
        tripType: customFilters?.tripType ?? selectedTripType,
        price: customFilters?.price ?? selectedPrice,
        difficulty: customFilters?.difficulty ?? selectedDifficulty,
        duration: customFilters?.duration ?? selectedDuration,
      }

      try {
        // Luôn dùng pagination với pageSize = 9
        const response = await fetchToursV2(filter, undefined, undefined, page, pageSize)
        const data = (response as { data: TourItem[]; meta?: { pagination?: { total?: number } } }).data
        const meta = (response as { data: TourItem[]; meta?: { pagination?: { total?: number } } }).meta

        transformApiDataTours(response ? data : [])
        if (meta?.pagination?.total !== undefined) {
          setTotalTours(meta.pagination.total)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      setLang(document.documentElement.lang)
      fetchDestinationsList()
    }, [])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        if (!target.closest(".relative")) {
          const dropdowns = document.querySelectorAll(".dropdown-menu")
          dropdowns.forEach(dropdown => {
            dropdown.classList.add("hidden")
          })
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }, [])

    const fetchDestinationsList = async () => {
      try {
        const response = await fetchDestinations({ pageSize: "100" })
        const data = (response as { data: DestinationItem[] }).data
        setDestinations(data || [])
      } catch (error) {
        console.error("Error fetching destinations:", error)
      }
    }

    const handleApplyFilters = () => {
      setLoading(true)
      setCurrentPage(1)
      fetchToursList(searchValue, undefined, 1)
    }

    const handleResetFilters = () => {
      setSelectedDestination([])
      setSelectedTripType([])
      setSelectedPrice([])
      setSelectedDifficulty([])
      setSelectedDuration([])
      setCurrentPage(1)

      fetchToursList(
        searchValue,
        {
          destination: [],
          tripType: [],
          price: [],
          difficulty: [],
          duration: [],
        },
        1,
      )
    }

    const handlePageChange = (page: number) => {
      setCurrentPage(page)
      fetchToursList(searchValue, undefined, page)
      // Scroll to top khi chuyển trang
      window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const closeAllDropdowns = () => {
      const dropdowns = document.querySelectorAll(".dropdown-menu")
      dropdowns.forEach(dropdown => {
        dropdown.classList.add("hidden")
      })
    }

    const toggleDropdown = (dropdownId: string) => {
      closeAllDropdowns()
      const dropdown = document.getElementById(dropdownId)
      if (dropdown) {
        dropdown.classList.toggle("hidden")
      }
    }

    useEffect(() => {
      setCurrentPage(1)
      if (searchValue) {
        fetchToursList(searchValue, undefined, 1)
      } else fetchToursList(undefined, undefined, 1)
    }, [categoryId, searchValue])

    const handleScroll = useCallback(() => {
      if (filterRef.current && filterInitialTopRef.current !== null) {
        const header = document.getElementById("site-header") as HTMLDivElement | null
        if (!header) return

        const headerHeight = header.offsetHeight ?? 70
        const paddingTop = parseFloat(window.getComputedStyle(filterRef.current).paddingTop || "0") || 0

        // Sử dụng vị trí ban đầu đã lưu để tính threshold
        const threshold = filterInitialTopRef.current + paddingTop - headerHeight

        if (window.scrollY >= threshold) {
          setIsScrolledToFilter(true)
          header.style.transform = "translateY(-82px)"
          header.style.transition = "transform 0.3s ease"
        } else {
          setIsScrolledToFilter(false)
          header.style.transform = "translateY(0)"
          header.style.transition = "transform 0.3s ease"
        }
      }
    }, [])

    // Lưu vị trí ban đầu của filter khi component mount
    useEffect(() => {
      if (filterRef.current && filterInitialTopRef.current === null) {
        const filterRect = filterRef.current.getBoundingClientRect()
        filterInitialTopRef.current = filterRect.top + window.scrollY
      }
    }, [])

    useEffect(() => {
      window.addEventListener("scroll", handleScroll, { passive: true })

      // Gọi handleScroll sau một chút để đảm bảo filterInitialTopRef đã được set
      const timeoutId = setTimeout(() => {
        handleScroll()
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener("scroll", handleScroll)
        // Reset header transform khi component unmount
        const header = document.getElementById("site-header") as HTMLDivElement | null
        if (header) {
          header.style.transform = ""
          header.style.transition = ""
        }
      }
    }, [handleScroll])

    return (
      <>
        <div id="all-tours" className="pt-0 lg:pt-16">
          <div
            ref={filterRef}
            className={`flex flex-col gap-3 sm:sticky bg-white z-50 pb-4 transition-all duration-300 ${
              isScrolledToFilter ? "top-0" : "top-[4.125rem] lg:top-[4.375rem] "
            }`}
          >
            <p className="text-xl leading-[30px] font-semibold text-[#4F4F4F]">{t("Filter by:")}</p>

            <div className="flex w-full sm:flex-row flex-col sm:justify-between gap-6">
              <div className={`flex ${showDurationFilter ? "gap-1.5" : "gap-4"} sm:flex-row flex-col`}>
                {/* Destination Select */}
                <div className="relative">
                  <div
                    className={`custom-select ${selectedDestination.length > 0 ? "has-selection" : ""}`}
                    onClick={() => toggleDropdown("destination-dropdown")}
                  >
                    {t("Destination")}
                    {selectedDestination.length > 0 && (
                      <span className="select-badge">{selectedDestination.length}</span>
                    )}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <div id="destination-dropdown" className="dropdown-menu hidden">
                    {destinations.map(destination => (
                      <div
                        key={destination.id}
                        className={`dropdown-option ${
                          selectedDestination.includes(destination.attributes.name) ? "selected" : ""
                        }`}
                        onClick={() => {
                          const newSelection = selectedDestination.includes(destination.attributes.name)
                            ? selectedDestination.filter(item => item !== destination.attributes.name)
                            : [...selectedDestination, destination.attributes.name]
                          setSelectedDestination(newSelection)
                        }}
                      >
                        {destination.attributes.name}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Duration Select - chỉ hiển thị khi URL chứa all-tours */}
                {showDurationFilter && (
                  <div className="relative">
                    <div
                      className={`custom-select ${selectedDuration.length > 0 ? "has-selection" : ""}`}
                      onClick={() => toggleDropdown("duration-dropdown")}
                    >
                      {t("Duration")}
                      {selectedDuration.length > 0 && <span className="select-badge">{selectedDuration.length}</span>}
                      <ChevronDown size={20} />
                    </div>
                    <div id="duration-dropdown" className="dropdown-menu hidden">
                      {durationOptions.map((option: { value: string; labelKey: string }) => (
                        <div
                          key={option.value}
                          className={`dropdown-option ${selectedDuration.includes(option.value) ? "selected" : ""}`}
                          onClick={() => {
                            const newSelection = selectedDuration.includes(option.value)
                              ? selectedDuration.filter(item => item !== option.value)
                              : [...selectedDuration, option.value]
                            setSelectedDuration(newSelection)
                          }}
                        >
                          {t(option.labelKey)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trip Types Select */}
                <div className="relative">
                  <div
                    className={`custom-select ${selectedTripType.length > 0 ? "has-selection" : ""}`}
                    onClick={() => toggleDropdown("trip-type-dropdown")}
                  >
                    {t("Trip Types")}
                    {selectedTripType.length > 0 && <span className="select-badge">{selectedTripType.length}</span>}
                    <ChevronDown size={20} />
                  </div>
                  <div id="trip-type-dropdown" className="dropdown-menu hidden">
                    {tripTypes.map(option => (
                      <div
                        key={option.value}
                        className={`dropdown-option ${selectedTripType.includes(option.value) ? "selected" : ""}`}
                        onClick={() => {
                          const newSelection = selectedTripType.includes(option.value)
                            ? selectedTripType.filter(item => item !== option.value)
                            : [...selectedTripType, option.value]
                          setSelectedTripType(newSelection)
                        }}
                      >
                        {t(option.labelKey)}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price Select */}
                <div className="relative">
                  <div
                    className={`custom-select ${selectedPrice.length > 0 ? "has-selection" : ""}`}
                    onClick={() => toggleDropdown("price-dropdown")}
                  >
                    {t("Price")}
                    {selectedPrice.length > 0 && <span className="select-badge">{selectedPrice.length}</span>}
                    <ChevronDown size={20} />
                  </div>
                  <div id="price-dropdown" className="dropdown-menu hidden">
                    {priceRangesData.map(option => (
                      <div
                        key={option.value}
                        className={`dropdown-option ${selectedPrice.includes(option.value) ? "selected" : ""}`}
                        onClick={() => {
                          const newSelection = selectedPrice.includes(option.value)
                            ? selectedPrice.filter(item => item !== option.value)
                            : [...selectedPrice, option.value]
                          setSelectedPrice(newSelection)
                        }}
                      >
                        {t(option.labelKey)}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Difficulty Select */}
                <div className="relative">
                  <div
                    className={`custom-select ${selectedDifficulty.length > 0 ? "has-selection" : ""}`}
                    onClick={() => toggleDropdown("difficulty-dropdown")}
                  >
                    {t("Difficulty")}
                    {selectedDifficulty.length > 0 && <span className="select-badge">{selectedDifficulty.length}</span>}
                    <ChevronDown size={20} />
                  </div>
                  <div id="difficulty-dropdown" className="dropdown-menu hidden">
                    {difficultyLevels.map(option => (
                      <div
                        key={option.value}
                        className={`dropdown-option ${selectedDifficulty.includes(option.value) ? "selected" : ""}`}
                        onClick={() => {
                          const newSelection = selectedDifficulty.includes(option.value)
                            ? selectedDifficulty.filter(item => item !== option.value)
                            : [...selectedDifficulty, option.value]
                          setSelectedDifficulty(newSelection)
                        }}
                      >
                        {t(option.labelKey)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex sm:border-l justify-end border-[#CCCCCC]">
                <button
                  onClick={handleApplyFilters}
                  className="sm:ml-6 px-4 py-2 border border-[#CCCCCC] rounded-full bg-white text-[#4F4F4F] text-base font-semibold md:hover:bg-gray-100 transition-colors"
                >
                  {t("Apply")}
                </button>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 text-[#4F4F4F] text-base font-semibold transition-colors md:hover:text-[#0B9444]"
                >
                  {t("Reset")}
                </button>
              </div>
            </div>
          </div>
          {showTitle && (
            <div className="pt-10 pb-6 text-[#242F3E] text-2xl leading-7 font-semibold">
              {totalTours > 0 ? totalTours : toursData.length} {t("Trips")}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-[70vh] w-full">
              <Spin indicator={<LoadingOutlined spin style={{ color: "#0B9444", fontSize: 48 }} />} />
            </div>
          ) : (
            <>
              {toursData && toursData.length > 0 ? (
                <>
                  <div className="flex flex-col gap-10 sm:grid sm:grid-cols-3">
                    {toursData.map((tourDetail, index) => (
                      <div key={index} className="h-full">
                        <TourCard tourDetail={tourDetail} />
                      </div>
                    ))}
                  </div>
                  {totalTours > pageSize && (
                    <div className="flex justify-center mt-10">
                      <Pagination
                        current={currentPage}
                        total={totalTours}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                        // showQuickJumper
                        showTotal={(total, range) => `${range[0]}-${range[1]} ${t("of")} ${total} ${t("tours")}`}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-[30vh] w-full">
                  <p>{t("No tour found, contact us for more information")}</p>
                </div>
              )}
            </>
          )}
        </div>
      </>
    )
  },
}
